/**
 * Voice widget UI store — Zustand 5 + immer.
 *
 * Holds the *client-side* call/widget state that must survive component
 * remounts across route navigation (the floating widget is mounted once in
 * `AppLayout`, but React still remounts subtrees on some layout changes).
 *
 * Only the widget CHROME (position, minimized) and the agent's caller-ID
 * selection are persisted — `partialize` below. Call state is deliberately
 * transient: an in-progress WebRTC call cannot survive a hard page refresh
 * regardless of what we remember, so it resets to idle on reload and the
 * agent redials if needed.
 *
 * Server call data (call history, analytics, recordings) belongs to
 * TanStack Query, never here — this store is exclusively the live call's
 * transient state and the widget's own chrome.
 */
import '@/lib/immer-setup';

import { enableMapSet } from 'immer';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type {
    CallDirection,
    CallStatus,
    ICallIncomingPayload,
    ICallParkedPayload,
    IHeldCall,
    IRingingCall,
    IVoiceChannelIdentity,
    IVoiceContactSnapshot,
} from '../types';

// This store puts a `Set` into immer drafts (the dedup tape). Immer refuses to
// draft one unless its MapSet plugin is loaded — the failure is an opaque
// "[Immer] minified error nr: 0" thrown from `recordEvent`. Idempotent, and
// called at module load so it runs before the store below is created. The
// inbox store calls it too; neither may rely on the other having been imported.
enableMapSet();

/** Dedup tape size — voice event volume per session is low, unlike inbox. */
const RECENT_EVENT_IDS_LIMIT = 100;

/**
 * Idle widget tabs ('keypad' | 'history' | 'contacts'), the idle per-number
 * drill-in ('historyDetail', target in `historyDetailTarget`), and in-call
 * sub-panels ('keypad' = DTMF overlay, 'transfer'). The same 'keypad' value
 * serves both surfaces — which one renders is decided by whether a call is
 * active, never by the panel value alone. `null` = the surface's default view.
 */
export type VoiceWidgetPanel =
    | 'keypad'
    | 'history'
    | 'contacts'
    | 'transfer'
    | 'historyDetail'
    | null;

/**
 * Target of the History per-number detail view. Never persisted (partialize
 * is a whitelist) — a reload lands back on the idle default.
 */
export interface IHistoryDetailTarget {
    readonly number: string;
    /** Snapshot from the row that opened the view — optimistic header label
     *  while the contacts lookup resolves. */
    readonly contact: IVoiceContactSnapshot | null;
}

/** Softphone readiness: 'ready' = registered with the provider, 'offline' = was
 *  expected to be reachable but isn't (recovery runs in the background). */
export type VoiceDeviceStatus = 'connecting' | 'ready' | 'offline';

export interface IActiveCall {
    readonly callId: string;
    readonly direction: CallDirection;
    readonly status: CallStatus;
    readonly contact: IVoiceContactSnapshot | null;
    /** The org line this call runs on — drives the widget header label. */
    readonly channelIdentity: IVoiceChannelIdentity | null;
    readonly fromNumber: string;
    readonly toNumber: string;
    readonly answeredAt: string | null;
    readonly onHold: boolean;
    readonly muted: boolean;
    readonly recordingEnabled: boolean;
    readonly transferCount: number;
}

/**
 * A dial asked for from OUTSIDE the widget (e.g. `ContactDialer` on a contact
 * card or the inbox header). `useVoiceSession` owns the device+socket and is
 * mounted once, in `CallWidget` — so callers publish a request here and the
 * widget performs the dial through its own `handleDial` (re-entry + minutes
 * guards included) and clears it. Never persisted: a pending dial must not
 * survive a reload and auto-call someone.
 */
export interface IDialRequest {
    readonly toNumber: string;
    /** Links the call to the contact server-side. */
    readonly contactId?: string;
    /** Optimistic label so the widget shows a name instead of bare digits. */
    readonly contact?: IVoiceContactSnapshot;
    /** Per-call caller ID — does NOT touch the persisted `selectedFromIdentityId`. */
    readonly fromIdentityId?: string;
}

/**
 * An answer/decline asked for from the header badge (`CallHeaderBadge` lives
 * in the app header, outside the widget). `useVoiceSession` owns the device
 * and is mounted once, in `CallWidget` — so the badge publishes a request
 * here and the widget performs it and clears it (mirrors `dialRequest`).
 * Never persisted: a pending answer must not survive a reload.
 */
export interface IRingingActionRequest {
    readonly action: 'answer' | 'decline';
    readonly callId: string;
}

export interface IWidgetPosition {
    readonly x: number;
    readonly y: number;
}

export interface IWidgetSize {
    readonly width: number;
    readonly height: number;
}

interface IRecentEvents {
    readonly set: ReadonlySet<string>;
    readonly queue: readonly string[];
}

export interface IVoiceUiState {
    // --- Widget chrome ------------------------------------------------------
    minimized: boolean;
    position: IWidgetPosition | null;
    /** Explicit size from the resize grip; null = the design size (w-80 +
     *  natural content height). Applied as width + min-height so no panel
     *  state can ever be clipped below its content. */
    size: IWidgetSize | null;
    activePanel: VoiceWidgetPanel;
    /** Counterpart shown by the 'historyDetail' panel; null otherwise. */
    historyDetailTarget: IHistoryDetailTarget | null;

    /**
     * Number typed into the dialer but not yet placed. Lives here rather than
     * in `DialerPanel` because minimizing unmounts the whole widget subtree
     * (`if (minimized) return null`) — local state was silently discarded, so
     * reopening the dialer lost a half-typed number. Deliberately NOT
     * persisted: it survives minimize and tab switches within the session,
     * and a page reload starts clean.
     */
    dialerDraft: string;

    /** Persisted caller-ID choice (channel identity id); null = "Auto"
     *  (server picks via its fallback chain). */
    selectedFromIdentityId: string | null;

    // --- Device readiness -----------------------------------------------
    deviceStatus: VoiceDeviceStatus;
    /** Why the device is offline, when the server told us something actionable
     *  (a 4xx from /voice/token — e.g. voice not enabled, or provisioned
     *  against a provider the org no longer uses). Null for transient network
     *  failures, which genuinely are just "reconnecting". Never persisted. */
    deviceError: string | null;

    // --- Call state -----------------------------------------------------
    /** Inbound calls ringing this agent, oldest first — the finish-first
     *  queue. Exactly one call can be ACTIVE; everything else waits here
     *  until answered (promoted), declined, answered elsewhere, or timed
     *  out server-side (`ringTimeoutSec`). */
    ringingQueue: IRingingCall[];
    /** Inbound callers parked on hold (all agents were busy), broadcast to
     *  every online agent — any of them can pick one up when free. */
    heldQueue: IHeldCall[];
    activeCall: IActiveCall | null;

    /** Set right after a call completes so the widget shows the inline
     *  post-call card (notes + optional disposition + recording); cleared once
     *  the agent closes it or answering/dialing dismisses it. */
    postCallCardCallId: string | null;

    /** An initiate round trip is in flight. Store-held rather than local widget
     *  state because End-during-dial reads it to decide whether a server row
     *  exists yet — a widget remount would silently reset a `useState` flag and
     *  send the hangup against a callId the server has never seen. */
    isDialing: boolean;

    /** A dial requested from outside the widget; the widget consumes + clears it. */
    dialRequest: IDialRequest | null;

    /** An answer/decline requested from the header badge; the widget consumes + clears it. */
    ringingAction: IRingingActionRequest | null;

    /** Dedup tape — first stage of the WS dedup chain (mirrors inbox). */
    recentEvents: IRecentEvents;

    // --- Actions ----------------------------------------------------------
    setMinimized: (minimized: boolean) => void;
    setPosition: (position: IWidgetPosition) => void;
    setSize: (size: IWidgetSize | null) => void;
    setActivePanel: (panel: VoiceWidgetPanel) => void;
    /** Number typed into the dialer; pass '' to clear it. */
    setDialerDraft: (value: string) => void;
    /** Open the per-number detail view (from a History row). */
    openHistoryDetail: (target: IHistoryDetailTarget) => void;
    /** Back to the History tab. */
    closeHistoryDetail: () => void;
    setSelectedFromIdentityId: (channelIdentityId: string | null) => void;
    /** `error` is only retained for the 'offline' status, and only when the
     *  server gave an actionable reason — see `deviceError`. */
    setDeviceStatus: (status: VoiceDeviceStatus, error?: string | null) => void;

    /** Adds a ringing call (dedup by callId — a redelivered WS event keeps
     *  the first entry). Deliberately does NOT un-minimize: while the widget
     *  is closed the header badge (`CallHeaderBadge`) surfaces the ring. */
    enqueueRinging: (payload: ICallIncomingPayload) => void;
    /** Idempotent — removing an absent callId is a no-op. */
    removeRinging: (callId: string) => void;
    /** Drops every queued ringing call — used on device teardown so store
     *  rows never outlive the SDK legs backing them. */
    clearRingingQueue: () => void;

    /** Adds a parked caller to the hold queue (dedup by callId, un-minimizes). */
    enqueueHeld: (payload: ICallParkedPayload) => void;
    /** Idempotent — removing an absent callId is a no-op. */
    removeHeld: (callId: string) => void;
    clearHeldQueue: () => void;

    setActiveCall: (call: IActiveCall | null) => void;
    patchActiveCall: (callId: string, patch: Partial<IActiveCall>) => void;

    setPostCallCardCallId: (callId: string | null) => void;

    setIsDialing: (isDialing: boolean) => void;

    /** Ask the widget to place a call (and un-minimize so the agent sees it). */
    requestDial: (request: IDialRequest) => void;
    /** Idempotent — the widget clears the request before dialing. */
    clearDialRequest: () => void;

    /** Ask the widget to answer/decline a ringing call (from the header badge). */
    requestRingingAction: (request: IRingingActionRequest) => void;
    /** Idempotent — the widget clears the request before acting. */
    clearRingingAction: () => void;

    hasSeenEvent: (eventId: string) => boolean;
    recordEvent: (eventId: string) => void;

    /** Full reset — used on logout. */
    reset: () => void;
}

function makeInitialState(): Pick<
    IVoiceUiState,
    | 'minimized'
    | 'position'
    | 'size'
    | 'activePanel'
    | 'historyDetailTarget'
    | 'dialerDraft'
    | 'selectedFromIdentityId'
    | 'deviceStatus'
    | 'deviceError'
    | 'ringingQueue'
    | 'heldQueue'
    | 'activeCall'
    | 'postCallCardCallId'
    | 'isDialing'
    | 'dialRequest'
    | 'ringingAction'
    | 'recentEvents'
> {
    return {
        minimized: true,
        position: null,
        size: null,
        activePanel: null,
        historyDetailTarget: null,
        dialerDraft: '',
        selectedFromIdentityId: null,
        deviceStatus: 'connecting',
        deviceError: null,
        ringingQueue: [],
        heldQueue: [],
        activeCall: null,
        postCallCardCallId: null,
        isDialing: false,
        dialRequest: null,
        ringingAction: null,
        recentEvents: { set: new Set<string>(), queue: [] },
    };
}

export const useVoiceStore = create<IVoiceUiState>()(
    persist(
        immer((set, get) => ({
            ...makeInitialState(),

            setMinimized: (minimized) =>
                set((s) => {
                    s.minimized = minimized;
                }),

            setPosition: (position) =>
                set((s) => {
                    s.position = position;
                }),

            setSize: (size) =>
                set((s) => {
                    s.size = size;
                }),

            setActivePanel: (panel) =>
                set((s) => {
                    s.activePanel = panel;
                }),

            setDialerDraft: (value) =>
                set((s) => {
                    s.dialerDraft = value;
                }),

            openHistoryDetail: (target) =>
                set((s) => {
                    s.historyDetailTarget = target;
                    s.activePanel = 'historyDetail';
                }),

            closeHistoryDetail: () =>
                set((s) => {
                    s.historyDetailTarget = null;
                    s.activePanel = 'history';
                }),

            setSelectedFromIdentityId: (channelIdentityId) =>
                set((s) => {
                    s.selectedFromIdentityId = channelIdentityId;
                }),

            setDeviceStatus: (status, error = null) =>
                set((s) => {
                    s.deviceStatus = status;
                    // A recovered device must not keep showing a stale reason.
                    s.deviceError = status === 'offline' ? error : null;
                }),

            enqueueRinging: (payload) =>
                set((s) => {
                    // A call already promoted to ACTIVE must never also show as
                    // a ringing row (a late/duplicate incoming event for the
                    // in-progress call would otherwise resurrect it in the queue).
                    if (s.activeCall?.callId === payload.callId) return;
                    // Dedup by callId — WS redelivery must not reset the
                    // entry's position or its original receivedAt.
                    if (s.ringingQueue.some((q) => q.callId === payload.callId)) return;
                    s.ringingQueue.push({ ...payload, receivedAt: Date.now() });
                }),

            removeRinging: (callId) =>
                set((s) => {
                    const index = s.ringingQueue.findIndex((q) => q.callId === callId);
                    if (index === -1) return;
                    s.ringingQueue.splice(index, 1);
                }),

            clearRingingQueue: () =>
                set((s) => {
                    s.ringingQueue = [];
                }),

            enqueueHeld: (payload) =>
                set((s) => {
                    if (s.activeCall?.callId === payload.callId) return;
                    if (s.heldQueue.some((h) => h.callId === payload.callId)) return;
                    s.heldQueue.push({ ...payload, heldSince: Date.now() });
                    // Surface the widget for idle agents only — the park event
                    // is an org-wide broadcast, and an agent mid-call must not
                    // have their in-call view forced open by it.
                    if (!s.activeCall) s.minimized = false;
                }),

            removeHeld: (callId) =>
                set((s) => {
                    const index = s.heldQueue.findIndex((h) => h.callId === callId);
                    if (index === -1) return;
                    s.heldQueue.splice(index, 1);
                }),

            clearHeldQueue: () =>
                set((s) => {
                    s.heldQueue = [];
                }),

            setActiveCall: (call) =>
                set((s) => {
                    // Call-surface transition (start, end, or a new call):
                    // clear the panel so the idle 'keypad' tab never bleeds
                    // into the in-call DTMF sub-panel and stale sub-panels
                    // never survive past their call. 'history', 'contacts'
                    // and 'historyDetail' are unambiguous idle-only values —
                    // keep them so a dial from any of those surfaces returns
                    // there when the call ends.
                    const isTransition = call?.callId !== s.activeCall?.callId;
                    if (
                        isTransition &&
                        s.activePanel !== 'history' &&
                        s.activePanel !== 'contacts' &&
                        s.activePanel !== 'historyDetail'
                    ) {
                        s.activePanel = null;
                    }
                    s.activeCall = call;
                    if (call) {
                        // Only the answered/picked entry leaves the queues —
                        // other ringing/held calls keep waiting (finish-first).
                        // The queues also survive `setActiveCall(null)`.
                        const ringingIndex = s.ringingQueue.findIndex(
                            (q) => q.callId === call.callId,
                        );
                        if (ringingIndex !== -1) s.ringingQueue.splice(ringingIndex, 1);
                        const heldIndex = s.heldQueue.findIndex((h) => h.callId === call.callId);
                        if (heldIndex !== -1) s.heldQueue.splice(heldIndex, 1);
                        s.minimized = false;
                    }
                }),

            patchActiveCall: (callId, patch) =>
                set((s) => {
                    if (s.activeCall?.callId !== callId) return;
                    Object.assign(s.activeCall, patch);
                }),

            setIsDialing: (isDialing) =>
                set((s) => {
                    s.isDialing = isDialing;
                }),

            setPostCallCardCallId: (callId) =>
                set((s) => {
                    s.postCallCardCallId = callId;
                    if (callId) s.minimized = false;
                }),

            requestDial: (request) =>
                set((s) => {
                    s.dialRequest = request;
                    // Surface the widget so the agent sees the call they started
                    // from a contact card (mirrors `enqueueRinging`).
                    s.minimized = false;
                }),

            clearDialRequest: () =>
                set((s) => {
                    s.dialRequest = null;
                }),

            requestRingingAction: (request) =>
                set((s) => {
                    s.ringingAction = request;
                }),

            clearRingingAction: () =>
                set((s) => {
                    s.ringingAction = null;
                }),

            // O(1) membership via the Set — no array scan (mirrors inbox).
            hasSeenEvent: (eventId) => get().recentEvents.set.has(eventId),

            recordEvent: (eventId) =>
                set((s) => {
                    if (s.recentEvents.set.has(eventId)) return;
                    const nextSet = new Set(s.recentEvents.set);
                    nextSet.add(eventId);
                    const nextQueue = [...s.recentEvents.queue, eventId];
                    while (nextQueue.length > RECENT_EVENT_IDS_LIMIT) {
                        const evicted = nextQueue.shift();
                        if (evicted !== undefined) nextSet.delete(evicted);
                    }
                    s.recentEvents = { set: nextSet, queue: nextQueue };
                }),

            reset: () =>
                set((s) => {
                    Object.assign(s, makeInitialState());
                }),
        })),
        {
            name: 'voice-widget-ui',
            version: 1,
            // v0 positions were placed for the floating minimized pill, which
            // no longer exists (the launcher moved into the app header) — a
            // pill parked at the viewport edge would open the 320px panel
            // off-screen, so reset to the anchored-under-the-badge default.
            migrate: (persisted, version) => {
                if (version === 0 && persisted && typeof persisted === 'object') {
                    return { ...(persisted as Record<string, unknown>), position: null };
                }
                return persisted;
            },
            partialize: (s) => ({
                position: s.position,
                size: s.size,
                minimized: s.minimized,
                selectedFromIdentityId: s.selectedFromIdentityId,
            }),
        },
    ),
);
